'use client';

import React from 'react';
import { cn } from '@/shared/lib/cn';

/**
 * Ambient "the model is working" indicator for a card that is rendering.
 *
 * Design (variant B, shared spec for WEB/Apple/Android):
 * a single monochrome teal glow feathered inward from the card edge, breathing
 * on a 3.8 s smooth cycle, over a static hairline ring so the card still reads
 * as active at the bottom of the pulse. No travelling strands, no second hue,
 * no canvas — the card must stay quiet while the progress overlay carries the
 * information.
 *
 * Brand teal H=177 S=90: ring L=40% (#0AC2B9), glow core slightly lighter.
 */

interface AIGlowBorderProps {
  isActive: boolean;
  className?: string;
  children: React.ReactNode;
}

/** Bare overlay — for callers that already own the positioned container. */
export function AIGlowOverlay({
  isActive,
  className,
}: {
  isActive: boolean;
  className?: string;
}) {
  return (
    <div
      aria-hidden="true"
      className={cn(
        'pointer-events-none absolute inset-0 z-40 rounded-[inherit]',
        'transition-opacity duration-700 ease-out',
        isActive ? 'opacity-100' : 'opacity-0',
        className
      )}
    >
      {/* Static hairline: the card stays outlined through the whole pulse. */}
      <div className="absolute inset-0 rounded-[inherit] shadow-[inset_0_0_0_1px_rgba(10,194,185,0.45)]" />
      {/* Breathing glow, feathered inward from the edge. */}
      <div className="absolute inset-0 rounded-[inherit] shadow-[inset_0_0_34px_-4px_rgba(13,242,231,0.75),inset_0_0_80px_6px_rgba(10,194,185,0.42)] animate-ai-breathe" />
    </div>
  );
}

/** Wrapper variant — positions itself around `children`. */
export function AIGlowBorder({
  isActive,
  className,
  children,
}: AIGlowBorderProps) {
  return (
    <div className={cn('relative', className)}>
      {children}
      <AIGlowOverlay isActive={isActive} />
    </div>
  );
}
